interface GuideOverlayProps {
  rect: DOMRect | null;
}

const PADDING = 8;

/**
 * Dim everything except the anchor. One element painted over the anchor with a
 * huge spread shadow is cheaper and smoother than four backdrop panels, and it
 * keeps a single rounded cutout.
 *
 * Position comes from `transform`, not `left`/`top`, and only `transform` is
 * transitioned: the element carries a viewport-sized shadow, so animating
 * layout properties would force a layout and a full-viewport repaint on every
 * scroll frame of a tour step.
 *
 * z-[60] lifts it over everything the app stacks: the header (z-20), the
 * sidebar (z-10), and at z-50 the wizard's floating buttons and the support
 * banner, which is later in the DOM. Below that they stay bright and look
 * clickable. The popover is z-[60] too but portalled to the end of the body,
 * so it still paints on top; toasts (z-[100]) stay above both.
 *
 * Clicks need no layer of their own: the step popover is modal, so Radix
 * already blocks pointer events on the rest of the page while it is open.
 */
export function GuideOverlay({ rect }: GuideOverlayProps) {
  if (!rect) return null;

  return (
    <div
      className="pointer-events-none fixed left-0 top-0 z-[60] rounded-lg ring-2 ring-blue-400 transition-transform duration-200 will-change-transform"
      style={{
        transform: `translate(${rect.left - PADDING}px, ${rect.top - PADDING}px)`,
        width: rect.width + PADDING * 2,
        height: rect.height + PADDING * 2,
        boxShadow: '0 0 0 9999px rgba(15, 23, 42, 0.55)',
      }}
      aria-hidden="true"
    />
  );
}
